import type { AttendanceReportRules, AttendanceShiftView } from 'shared-models/browser';

type AttendancePayMinute = {
  salary: number;
  dayBonusRate: 0 | 0.5 | 1;
  overtimeBonusRate: 0 | 0.25 | 0.5;
};

export function resolveAttendanceReportRules(rules: AttendanceReportRules, employeeId: number | null): AttendanceReportRules {
  return rules.employeeRuleSets?.find((group) => employeeId !== null && group.employeeIds.includes(employeeId))?.rules ?? rules;
}

export function buildAttendancePayMinutes(
  shifts: AttendanceShiftView[],
  fallbackHourlyBase: number,
  rules: AttendanceReportRules,
  isNightWindowMinute: (date: Date) => boolean,
  resolveDayBonusRate: (date: Date) => 0 | 0.5 | 1,
): AttendancePayMinute[] {
  const threshold125Minutes = Math.max(0, Math.floor(rules.overtime125StartHours * 60));
  const threshold150Minutes = Math.max(threshold125Minutes, Math.floor(rules.overtime150StartHours * 60));
  const result: AttendancePayMinute[] = [];
  for (const shift of shifts) {
    if (shift.isOpen || !shift.clockOutAt) continue;
    const startMs = Date.parse(shift.clockInAt);
    const endMs = Date.parse(shift.clockOutAt);
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) continue;
    const maxMinutes = Math.max(0, Math.floor((endMs - startMs) / 60000));
    const shiftMinutes = Math.max(0, Math.floor(shift.durationMinutes));
    const minutesToCheck = Math.min(maxMinutes, shiftMinutes || maxMinutes);
    const dates = Array.from({ length: minutesToCheck }, (_, minuteIndex) => new Date(startMs + minuteIndex * 60000));
    const qualifiesForNightOvertime = rules.useNightShiftOvertimeRules
      && dates.filter(isNightWindowMinute).length >= 120;
    const threshold125 = qualifiesForNightOvertime ? Math.max(0, threshold125Minutes - 60) : threshold125Minutes;
    const salary = typeof shift.hourlySalary === 'number' ? shift.hourlySalary : fallbackHourlyBase;
    dates.forEach((date, minuteIndex) => {
      result.push({
        salary,
        dayBonusRate: resolveDayBonusRate(date),
        overtimeBonusRate: minuteIndex >= threshold150Minutes ? 0.5 : minuteIndex >= threshold125 ? 0.25 : 0,
      });
    });
  }
  return result;
}